import { z } from 'zod';
import { DEFAULT_LOCALE, isLocale, LOCALES, type Locale } from './locales.js';

/**
 * User-authored text that faces the public (event type names, confirmation
 * messages). Stored per locale; at least one locale must be present.
 */
export type LocalizedString = Partial<Record<Locale, string>>;

export const localizedStringSchema = z
  .object(Object.fromEntries(LOCALES.map((l) => [l, z.string().trim().max(2000).optional()])))
  .refine((value) => LOCALES.some((l) => Boolean(value[l])), {
    message: 'at_least_one_locale_required',
  }) as unknown as z.ZodType<LocalizedString>;

/**
 * Optional variant. An object with no text in any locale (the form's initial
 * `{}`) becomes an explicit `null`, so a partial update can clear the field,
 * while a key that is absent stays `undefined` and leaves it untouched. Reads
 * accept `null` too: that is what Mongo stores for an undefined field.
 */
export const optionalLocalizedStringSchema = z.preprocess((value) => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const hasText = Object.values(value as Record<string, unknown>).some(
      (v) => typeof v === 'string' && v.trim() !== '',
    );
    return hasText ? value : null;
  }
  return value;
}, localizedStringSchema.nullable().optional());

/**
 * Resolve a LocalizedString: exact locale → fallback (usually the host's
 * locale) → default locale → any non-empty value → empty string.
 */
export function pickLocale(
  value: LocalizedString | null | undefined,
  locale: Locale,
  fallback: Locale = DEFAULT_LOCALE,
): string {
  if (!value) return '';
  return (
    value[locale] ||
    value[fallback] ||
    value[DEFAULT_LOCALE] ||
    LOCALES.map((l) => value[l]).find((v): v is string => Boolean(v)) ||
    ''
  );
}

export function missingLocales(value: LocalizedString): Locale[] {
  return LOCALES.filter((l) => !value[l]);
}

/**
 * Whether a value is user-authored text that still needs a locale picked.
 *
 * Strict on purpose: every key must be a supported locale and every value a
 * string. An arbitrary object with an `en` field is not one of these, and
 * mistaking it for one would resolve it to a fragment of itself.
 */
export function isLocalizedString(value: unknown): value is LocalizedString {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const entries = Object.entries(value);
  if (entries.length === 0) return false;
  return entries.every(([k, v]) => isLocale(k) && (v === undefined || typeof v === 'string'));
}
